import { useState, useEffect } from 'react';
import axios from 'axios';
import { InteractionSummaryData } from '../services/types';
import * as Sentry from '@sentry/react';
import { useYearTypeContext } from '../context/YearTypeContext';

export const useFetchInteractionSummaryData = (year: string, partnerId: string, initiativeId?: string) => {
    const { yearType } = useYearTypeContext();
    const [interactionSummary, setInteractionSummary] = useState<InteractionSummaryData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
  
    useEffect(() => {
        // Only fetch the interaction summary data if the year (if applicable) is valid
        if (!year || !partnerId) {
            setLoading(false);
            return;
        }

        const fetchInteractionSummary = async () => {
            const apiUrl = `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getinteractionsummarydata`;

            // Construct params object conditionally
            const params: Record<string, string> = { year };
            // Only add partnerId to params if it's not either of the Admin id's
            if (partnerId && partnerId !== '-1' && partnerId !== '-2') {
                params.partnerId = partnerId;
            }

            // Conditionally add initiativeId if it's provided
            if (initiativeId) {
                params.initiativeId = initiativeId;
            }

            // Conditionally add yearType if it's defined (e.g., "q1", "q2", etc.)
            if (yearType) {
                params.yearType = yearType;
            }
            
            try {
            setLoading(true);
            const response = await axios.get(apiUrl, { params });
            if (response.status === 200) {
                setInteractionSummary(response.data.data);
            } else {
                // Log the error response to Sentry
                Sentry.captureMessage(`Error fetching interaction summary data: ${response.data.message}`, 'warning');
                setError('Failed to fetch interaction summary data.');
            }
            } catch (err) {
                // Log the caught error to Sentry
                Sentry.captureException(error);
                setError('An error occurred while fetching the data.');
            } finally {
                setLoading(false);
            }
        };
    
        fetchInteractionSummary();
    }, [year, partnerId, initiativeId]);
  
    return { interactionSummary, loading, error };
};
